import {
  ChangeEvent,
  forwardRef,
  FunctionComponent,
  KeyboardEvent,
  PropsWithoutRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import Button from '../Button';
import Icon, { registerIcon } from '../Icon';
import * as plusIcon from '../Icon/icons/plus.icon';
import * as minusIcon from '../Icon/icons/minus.icon';
import Text from '../Text';
import Flex from '../Flex';
import FormField from '../FormField';
import { ForwardProps } from '../../types';
import Input from '../Input';
import { useConfiguration, useConsolidatedRef, useI18n, useUID } from '../../hooks';

import StyledNumberInput, { StyledStepperInput } from './NumberInput.styles';
import NumberInputProps, { NUMBER_MAX_DECIMAL_PLACES } from './NumberInput.types';
import {
  getCleanedValue,
  getDecimal,
  getDecrementedValue,
  getFormattedValue,
  getIncrementedValue,
  getInteger
} from './utils';

registerIcon(minusIcon, plusIcon);

const NumberInput: FunctionComponent<NumberInputProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<NumberInputProps>, ref: NumberInputProps['ref']) => {
    const uid = useUID();
    const {
      id = uid,
      status,
      label,
      labelHidden,
      info,
      required,
      disabled,
      readOnly,
      value = '',
      onChange,
      onBlur,
      unit,
      unitPlacement = 'after',
      showDecimal = true,
      numberOfDecimals: numberOfDecimalsProp = NUMBER_MAX_DECIMAL_PLACES,
      showGroupSeparators = true,
      min = Number.MIN_SAFE_INTEGER,
      max = Number.MAX_SAFE_INTEGER,
      step = 1,
      variant,
      'aria-label': ariaLabel,
      ...restProps
    } = props;

    const { locale } = useConfiguration();
    const [cursor, setCursor] = useState(0);
    const inputRef = useConsolidatedRef(ref);
    const containerRef = useRef<HTMLElement>(null);
    const numberOfDecimals = useMemo(
      () => (showDecimal ? numberOfDecimalsProp : 0),
      [showDecimal, numberOfDecimalsProp]
    );

    useEffect(() => {
      inputRef.current?.setSelectionRange(cursor, cursor);
    }, [cursor]);

    const formattedValue = useMemo(
      () =>
        getFormattedValue(
          !showDecimal ? getInteger(value) : value,
          locale,
          showGroupSeparators,
          numberOfDecimals
        ),
      [showDecimal, value, numberOfDecimals, showGroupSeparators, locale]
    );

    const onInputChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const selectionStart = e.target.selectionStart;
        const decimal = getDecimal(locale);
        const cleanedInputValue = getCleanedValue(inputValue, decimal, numberOfDecimals);
        if (cleanedInputValue === value) return;
        onChange?.(cleanedInputValue);
        if (selectionStart !== undefined && selectionStart !== null) {
          const formattedVal = getFormattedValue(
            cleanedInputValue,
            locale,
            showGroupSeparators,
            numberOfDecimals
          );
          const cursorPosition =
            selectionStart + (formattedVal.length - e.target.value.length) || 1;
          setCursor(cursorPosition);
        }
      },
      [onChange, locale, value, numberOfDecimals, showDecimal, cursor, showGroupSeparators]
    );

    const onKeyDown = useCallback(
      (ev: KeyboardEvent<HTMLInputElement>) => {
        if (readOnly) return;
        switch (ev.key) {
          case 'ArrowUp':
            ev.preventDefault();
            onChange?.(getIncrementedValue(value, min, max, step));
            break;
          case 'ArrowDown':
            ev.preventDefault();
            onChange?.(getDecrementedValue(value, min, max, step));
            break;
          default:
        }
      },
      [value, min, max, step, onChange]
    );

    const onInputBlur = useCallback(() => {
      let parsableValue = value;
      if (Number.isNaN(parseFloat(value))) {
        parsableValue = '';
      } else if (value?.endsWith('.')) {
        parsableValue = value.slice(0, -1);
      }
      onBlur?.(parsableValue);
    }, [value]);

    const t = useI18n();

    const NumberInputComp = (
      <>
        {unit && unitPlacement === 'before' && <Text>{unit}</Text>}
        <Input
          inputMode='numeric'
          ref={inputRef}
          value={formattedValue}
          onKeyDown={onKeyDown}
          onBlur={onInputBlur}
          aria-label={`${ariaLabel || label}. ${unit ? t('measured_in', [unit]) : ''}`}
          {...{ onChange: onInputChange, id, readOnly, required, disabled, status, ...restProps }}
        />
        {unit && unitPlacement === 'after' && <Text>{unit}</Text>}
      </>
    );

    const onMinusClick = () => {
      onChange?.(getDecrementedValue(value, min, max, step));
    };

    const onPlusClick = () => {
      onChange?.(getIncrementedValue(value, min, max, step));
    };

    const StepperComp = (
      <Flex
        as={StyledStepperInput}
        container={{ alignItems: 'center', wrap: 'nowrap' }}
        status={status}
        readOnly={readOnly}
        disabled={disabled}
        size={formattedValue?.length}
        unitPlacement={unitPlacement}
      >
        <Button
          variant='simple'
          icon
          onClick={onMinusClick}
          aria-label={t('number_decrement_value_by', [step])}
          readOnly={readOnly}
          disabled={disabled}
          tabIndex={-1}
        >
          <Icon name='minus' />
        </Button>
        <Flex
          container={{ alignItems: 'center', wrap: 'nowrap', justify: 'center' }}
          item={{ grow: 1 }}
          ref={containerRef}
          onClick={(e: MouseEvent) => {
            if (e.target === containerRef.current) inputRef?.current?.focus();
          }}
        >
          {NumberInputComp}
        </Flex>
        <Button
          variant='simple'
          icon
          onClick={onPlusClick}
          aria-label={t('number_increment_value_by', [step])}
          readOnly={readOnly}
          disabled={disabled}
          tabIndex={-1}
        >
          <Icon name='plus' />
        </Button>
      </Flex>
    );

    const NumberComp = (
      <Flex
        as={StyledNumberInput}
        container={{ alignItems: 'center', wrap: 'nowrap' }}
        status={status}
        readOnly={readOnly}
        disabled={disabled}
      >
        {NumberInputComp}
      </Flex>
    );

    const Comp = variant === 'stepper' && !readOnly ? StepperComp : NumberComp;

    return label ? (
      <FormField {...{ label, labelHidden, id, info, status, required, disabled }}>
        {Comp}
      </FormField>
    ) : (
      Comp
    );
  }
);

export default NumberInput;
